import { ReactNode, useEffect, useRef, useState } from "react"
import useMediaQuery from "utils/useMediaQuery"

interface LazyMountProps {
  children: ReactNode
  defaultHeight?: number
  visibleOffset?: number
  stayRendered?: boolean
}

export function LazyMount({
  children,
  defaultHeight = 300,
  visibleOffset = 0,
  stayRendered = false,
}: LazyMountProps) {
  const [isVisible, setIsVisible] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const margin = visibleOffset < 0 ? `${visibleOffset}px` : `${visibleOffset}px`
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          if (!stayRendered) observer.disconnect()
        } else if (!stayRendered) {
          setIsVisible(false)
        }
      },
      { rootMargin: margin },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [visibleOffset, stayRendered])

  return (
    <div ref={ref}>
      {isVisible ? children : <div style={{ height: defaultHeight }} />}
    </div>
  )
}

interface Props {
  i: number
  children: ReactNode
  defaultHeight?: number
  visibleOffset?: number
}

const MountIfVisible = (props: Props) => {
  const { i, children, defaultHeight, visibleOffset } = props
  const isDesktop = useMediaQuery("(min-width: 900px)")
  const limit = isDesktop ? 8 : 3
  return (
    <div>
      {i > limit ? (
        <LazyMount defaultHeight={defaultHeight} visibleOffset={visibleOffset}>
          {children}
        </LazyMount>
      ) : (
        children
      )}
    </div>
  )
}

export default MountIfVisible
